import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Features from "@/components/Features";
import Technology from "@/components/Technology";
import Testimonials from "@/components/Testimonials";
import CallToAction from "@/components/CallToAction";
import Footer from "@/components/Footer";
import Markets from "@/components/Markets";
import LiveProductChart from "@/components/LiveProductChart";
import ApiStatusPanel from "@/components/ApiStatusPanel";
import EcommerceLLM from "@/components/EcommerceLLM";
import CompetitiveAnalysisChart from "@/components/CompetitiveAnalysisChart";
import { Helmet } from "react-helmet";

const Index = () => {
  // Smooth scroll to sections when clicking on navigation links
  useEffect(() => {
    const handleHashLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "A") {
        const link = target as HTMLAnchorElement;
        const href = link.getAttribute("href");
        
        if (href && href.startsWith("#")) {
          event.preventDefault();
          const targetId = href.substring(1);
          const targetElement = document.getElementById(targetId);
          
          if (targetElement) {
            window.scrollTo({
              top: targetElement.offsetTop - 80, // Adjust for header height
              behavior: "smooth"
            });
          }
        }
      }
    };

    document.addEventListener("click", handleHashLinkClick);
    
    return () => {
      document.removeEventListener("click", handleHashLinkClick);
    };
  }, []);

  // Handle initial hash in URL
  useEffect(() => {
    const handleInitialHash = () => {
      const hash = window.location.hash;
      if (hash) {
        const targetId = hash.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          setTimeout(() => {
            window.scrollTo({
              top: targetElement.offsetTop - 80, // Adjust for header height
              behavior: "smooth"
            });
          }, 100);
        }
      }
    };

    handleInitialHash();
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>benchr.ai - E-commerce Competitive Intelligence & AI-Powered Market Analysis Platform</title>
        <meta name="description" content="Optimize your e-commerce strategy with Benchr's AI-powered competitive intelligence platform. Get real-time price tracking, product analytics, and market insights from global e-commerce websites to make data-driven decisions and outperform competitors." />
        <meta name="keywords" content="e-commerce intelligence, competitive analysis, price monitoring, market trends, product analytics, benchmarking tool, AI-powered e-commerce, competitor tracking, pricing strategy, market share analysis" />
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "benchr.ai - E-commerce Competitive Intelligence & AI Market Analysis",
            "description": "Optimize your e-commerce strategy with real-time competitive intelligence including price monitoring, market analysis, and AI-powered insights",
            "keywords": ["e-commerce intelligence", "competitive analysis", "price monitoring", "market trends", "AI-powered analytics", "competitor tracking", "pricing strategy", "market share analysis"],
            "publisher": {
              "@type": "Organization",
              "name": "Benchr",
              "logo": {
                "@type": "ImageObject",
                "url": "/lovable-uploads/5b3fc61d-09cc-4356-aca7-ef3a45266769.png"
              }
            },
            "mainEntity": {
              "@type": "SoftwareApplication",
              "name": "Benchr Intelligence",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR"
              },
              "featureList": "Real-time price monitoring, Competitor product analysis, Market share tracking, AI-powered insights, Global e-commerce data"
            }
          }
        `}
        </script>
      </Helmet>
      <header>
        <div className="fade-in">
          <Navbar />
        </div>
      </header>
      <main className="flex-grow pt-16 md:pt-20 relative">
        <section id="hero" aria-labelledby="hero-heading" className="relative z-10">
          <div className="hero-animation">
            <h1 id="hero-heading" className="sr-only">Benchr - E-commerce Competitive Intelligence Platform</h1>
            <Hero />
          </div>
        </section>
        
        <section id="markets" aria-labelledby="markets-title">
          <div className="container mx-auto px-4 py-12">
            <Markets />
          </div>
        </section>
        
        <section id="features" aria-labelledby="features-title">
          <Features />
        </section>
        
        <section id="technology" aria-labelledby="technology-title">
          <Technology />
        </section>
        
        <section id="llm" aria-labelledby="llm-title" className="container mx-auto px-4 py-12">
          <h2 id="llm-title" className="text-3xl md:text-4xl font-bold text-center text-benchr-900 dark:text-white mb-10">
            <span className="text-gradient">LLM-Powered E-commerce Analysis</span>
          </h2>
          <EcommerceLLM />
        </section>
        
        <section id="real-time-data" aria-labelledby="real-time-data-title" className="bg-benchr-50 dark:bg-benchr-900/50 py-12">
          <div className="container mx-auto px-6">
            <h2 id="real-time-data-title" className="text-3xl md:text-4xl font-bold text-center text-benchr-900 dark:text-white mb-10">
              <span className="text-gradient">Real-time E-commerce Data Indexing</span>
            </h2>
          </div>
        </section>
        
        <section id="live-chart" aria-labelledby="live-chart-title" className="container mx-auto px-4 py-12">
          <h3 id="live-chart-title" className="text-2xl md:text-3xl font-bold text-center text-benchr-900 dark:text-white mb-8">
            <span className="text-gradient">Price Trend Analysis</span>
          </h3>
          <LiveProductChart />
        </section>
        
        <section id="market-position" aria-labelledby="market-position-title" className="container mx-auto px-4 py-12">
          <h3 id="market-position-title" className="text-2xl md:text-3xl font-bold text-center text-benchr-900 dark:text-white mb-8">
            <span className="text-gradient">Market Position</span>
          </h3>
          <CompetitiveAnalysisChart />
        </section>
        
        <section id="api-status" aria-labelledby="api-status-title" className="bg-benchr-50 dark:bg-benchr-900/50 py-12">
          <div className="container mx-auto px-6">
            <h2 id="api-status-title" className="text-3xl md:text-4xl font-bold text-center text-benchr-900 dark:text-white mb-10">
              <span className="text-gradient">Global E-commerce API Status</span>
            </h2>
          </div>
        </section>
        
        <section id="api-status-panel" aria-labelledby="api-status-panel-title" className="container mx-auto px-4 py-12">
          <h3 id="api-status-panel-title" className="text-2xl md:text-3xl font-bold text-center text-benchr-900 dark:text-white mb-8">
            <span className="text-gradient">API Connection Status</span>
          </h3>
          <ApiStatusPanel />
        </section>
        
        <section id="case-studies" aria-labelledby="case-studies-title">
          <Testimonials />
        </section>
        
        <section id="cta" aria-labelledby="cta-title">
          <CallToAction />
        </section>
      </main>
      <footer>
        <div className="fade-in" style={{ animationDelay: "300ms" }}>
          <Footer />
        </div>
      </footer>
    </div>
  );
};

export default Index;
